import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Updates document.title to "<title> · iTow" on mount and resets to the bare
 * app title on unmount.
 *
 * @param i18nKey    - Fallback i18n key used when the record identifier is not
 *                     yet available (e.g. while data is loading).
 * @param customTitle - Optional record-specific display string (e.g.
 *                     "Claim #12345"). When provided it overrides the i18n key
 *                     so the tab shows the actual record rather than the generic
 *                     page name. Falls back to the i18n key while undefined.
 * @param prefix     - Optional string prepended to the assembled title with a
 *                     trailing space (e.g. "★" so favorited records surface a
 *                     star in the browser tab). Has no effect when undefined or
 *                     empty string.
 */
export function usePageTitle(i18nKey: string, customTitle?: string, prefix?: string): void {
  const { t } = useTranslation();
  useEffect(() => {
    const appTitle = t('app.title');
    const localized = customTitle ?? t(i18nKey, { defaultValue: '' });
    const base = localized ? `${localized} · ${appTitle}` : appTitle;
    document.title = prefix ? `${prefix} ${base}` : base;
    return () => {
      document.title = appTitle;
    };
  }, [i18nKey, customTitle, prefix, t]);
}
